// 3rd Party CSS
import 'bootstrap/dist/css/bootstrap.min.css'
import '@mainstem/mainstem-react-app/dist/index.css'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'
// Local CSS
import './assets/scss/utilities.scss'
import './assets/scss/layout.scss'
import './assets/css/tailwind.css'
// React
import { Suspense, lazy, useCallback, useEffect, useState } from 'react'
// Routing
import { BrowserRouter } from 'react-router-dom'
// Microsoft - Application Insights
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { AppInsightsContext, ReactPlugin, withAITracking } from '@microsoft/applicationinsights-react-js'
// MainStem - UI
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardHeaderTitle,
  CenterScreen,
  ToastContainer,
  theme as MSTheme
} from '@mainstem/react-mainstem'
// DEPRECATE : MainStem
import { AlertVersionUpgrade, MainStemBase } from '@mainstem/mainstem-react-app'
// DEPRECATE : API
import { APIAuthLogout } from 'api/auth/logout'
import { apiAuthCurrentUser } from 'api/auth/current-user'
import { apiStatusCheck } from 'api/statusCheck'
// MainStem - API
import { MainStemAPIPlatformControllersPurchasingAuthCurrentUserAPIResponse } from 'api-new'
// Global - Context
import { AppContext, AppContextDetails, IAppContextIntegrations, IMessage } from 'context'
// Global - Config
import { HTTP, affiliateProgramUUID, baseURL, ecommerceURL } from 'config'
// Global - Pages
import PagePunchOutOrderFailed from 'pages/purchasing/punch-out/order-failed'
import PagePunchOutOrderGenerating from 'pages/purchasing/punch-out/order-generating'
// Local  - Components
import NavTopButtons from './components/nav-top/buttons'
import NavTopIcons from './components/nav-top/icons'
import NavTopCTA from './components/nav-top/cta'
import NavTopSearch from './components/nav-top/search'
import { links } from './components/nav-left/links'
import { navTopUserLinks } from './components/nav-top/links'
import { LayoutImpersonate } from './components/impersonate'
import { ErrorWrapper } from './components/error-wrapper'
import { DefaultTitle } from './components/default-title'
import { SearchLive } from './components/nav-top/buttons/components'
import { faSignIn } from '@fortawesome/pro-light-svg-icons'
import { FinancialDimensionCacheProvider } from 'components/FinancialDimension/Context'
import NavTopAlertBar from './components/nav-top/alert-bar'
import { BetaInvite } from './components/beta-invite'
import { initParticlesEngine } from '@tsparticles/react'
import { loadAll } from '@tsparticles/all'

const ThemeBold = lazy(() => import('./components/theme-bold'))
const ThemeWhite = lazy(() => import('./components/theme-white'))

const reactPlugin = new ReactPlugin()

declare global {
  interface Window {
    analytics: any
  }
}

const LeftNavOrganizationLogo = ({ logoURL }: { logoURL?: string }) => {
  useEffect(() => {
    if (!logoURL) {
      return
    }

    const applyOrganizationLogo = () => {
      const image = document.getElementById('ms-nav-left-branding-image') as HTMLImageElement | null
      if (image && image.getAttribute('src') !== logoURL) {
        image.setAttribute('src', logoURL)
      }
    }

    applyOrganizationLogo()

    const observer = new MutationObserver(applyOrganizationLogo)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] })

    return () => {
      observer.disconnect()
    }
  }, [logoURL])

  return null
}

const appInsights = new ApplicationInsights({
  config: {
    connectionString: import.meta.env.VITE_REACT_APP_APPLICATION_INSIGHTS,
    enableAutoRouteTracking: true,
    extensions: [reactPlugin as any]
  }
})

appInsights.loadAppInsights()

const Layout = () => {
  // Set the baseURL in local storage for use in @mainstem/react-mainstem
  window.localStorage.setItem('baseURL', baseURL)

  const [firstLoad, setFirstLoad] = useState<boolean>(true)
  const [theme, setTheme] = useState<string | undefined>(window.localStorage.getItem('theme'))
  const [checkedForInitialUser, setCheckedForInitialUser] = useState<boolean>(false)
  const [particlesInitiated, setParticlesInitiated] = useState<boolean>(false)
  const [loggedInUser, setLoggedInUser] = useState<
    MainStemAPIPlatformControllersPurchasingAuthCurrentUserAPIResponse | undefined
  >(undefined)
  const [userToken, setUserToken] = useState<string>()
  const [integrations, setIntegrations] = useState<IAppContextIntegrations>({})
  const [permissions, setPermissions] = useState<any>(false)
  const [leftLinks, setLeftLinks] = useState<any>([])
  const [messages, setMessages] = useState<Array<IMessage>>([])

  const addMessages = (messagesToAdd: IMessage[]) => {
    async function addMessageAsync() {
      setMessages([...messages, ...messagesToAdd])
    }
    addMessageAsync()
  }

  const contextValue = {
    addMessages,
    integrations,
    loggedInUser,
    messages,
    setIntegrations,
    setLoggedInUser,
    userToken,
    setUserToken,
    permissions,
    setPermissions,
    particlesInitiated
  } as AppContextDetails

  const handleStatusCheck = () => {
    apiStatusCheck().then((_apiResponse) => {
      //
    })
  }

  const handleGetCurrentUser = useCallback(() => {
    apiAuthCurrentUser().then(async (apiResponseCurrentUser) => {
      if (apiResponseCurrentUser.wasSuccessful) {
        await links(apiResponseCurrentUser).then((response) => {
          setLeftLinks(response)
        })
        if (apiResponseCurrentUser.allowPurchasingApp) {
          setLoggedInUser(apiResponseCurrentUser)
          setUserToken(window.localStorage.getItem('mainstem_user_token'))
        } else {
          const token = window.localStorage.getItem('mainstem_user_token')
          window.localStorage.removeItem('mainstem_user_token')
          window.location.href = ecommerceURL + '/account-sync/' + token
        }
      }
      setCheckedForInitialUser(true)
    })
  }, [])

  const handleLogin = async (token: any) => {
    window.localStorage.setItem('mainstem_user_token', token)
    HTTP.defaults.headers.Authorization = `Bearer ${token}`
    await handleGetCurrentUser()
    window.location.href = '/'
  }

  const handleLogout = () => {
    APIAuthLogout().then(() => {
      setLoggedInUser(undefined)
    })
  }

  const publicRoutes = [
    {
      path: '/punch-outs/order-failed/:buyersCookie?',
      component: <PagePunchOutOrderFailed />
    },
    {
      path: '/punch-outs/order-generating/:buyersCookie',
      component: <PagePunchOutOrderGenerating />
    },
    {
      path: '/auth/okta/',
      component: (
        <>
          <CenterScreen>
            <div style={{ maxWidth: 420 }}>
              <Card>
                <CardHeader
                  title={
                    <CardHeaderTitle>
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                        <div style={{ height: 10 }}>Login with</div>
                        <div>
                          <img
                            alt='Okta Logo'
                            src='https://ok12static.oktacdn.com/assets/img/logos/okta-logo.1e146cad5713da744492be95eb0f7793.png'
                            style={{ height: 15 }}
                          />
                        </div>
                        <div>&amp;</div>
                        <div>
                          <img alt='MainStem Logo' src={MSTheme.logos.mainstem.color} style={{ height: 15 }} />
                        </div>
                      </div>
                    </CardHeaderTitle>
                  }
                />
                <CardBody>
                  <p>
                    You are about to login with Okta and MainStem. This will allow you to access your account and
                    purchase products.
                  </p>
                  <a href='https://dev-65722672.okta.com/oauth2/v1/authorize?client_id=0oahlfuqayByKo0GU5d7&response_type=code&scope=openid%20email%20profile&redirect_uri=https://dev-api.mainstem.io/api/auth/okta/authorization-code/callback&state=state-296bc9a0-a2a2-4a57-be1a-d0e2fd9bb601&nonce=foo'>
                    <Button block color='fancy' icon={faSignIn}>
                      Login With Okta & MainStem
                    </Button>
                  </a>
                </CardBody>
              </Card>
            </div>
          </CenterScreen>
        </>
      )
    }
  ]

  const appSettings = {
    affiliateUUID: affiliateProgramUUID,
    appName: 'Purchase Pro™',
    baseURL,
    checkedForInitialUser,
    forceLogin: true,
    impersonationComponent: <LayoutImpersonate />,
    publicizedRoutes: publicRoutes,
    links: leftLinks,
    loggedInUser,
    logo:
      theme === 'ms-theme-1' ? 'https://templates.mainstem.io/apps/lib/components/Layout/NavTop/mainstem-logo.svg' : '',
    navTopAlertBar: <NavTopAlertBar />,
    navTopButtons: <NavTopButtons />,
    navTopCTA: <NavTopCTA />,
    navTopIcons: <NavTopIcons />,
    navTopMobile: (
      <>
        <div style={{ marginTop: 7, minWidth: '550px', width: '100%' }}>
          <SearchLive />
        </div>
      </>
    ),
    navTopSearch: <NavTopSearch />,
    navTopUserLinks: navTopUserLinks(loggedInUser),
    onLogin: handleLogin,
    onLogout: handleLogout,
    routes: <ErrorWrapper />,
    showNavLeft: true,
    theme: !theme ? 'ms-theme-2' : theme,
    userImage: loggedInUser?.profileImageURL || loggedInUser?.organization?.logoURL,
    userToken
  }

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadAll(engine)
    }).then(() => {
      setParticlesInitiated(true)
    })
  }, [])

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false)

      const themeFromLocal = window.localStorage.getItem('theme')

      if (themeFromLocal) {
        setTheme(themeFromLocal)
      }

      handleGetCurrentUser()
      handleStatusCheck()
    }
  }, [firstLoad, handleGetCurrentUser])

  return (
    <AppContext.Provider value={contextValue}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <FinancialDimensionCacheProvider>
          {theme === 'ms-theme-1' ? (
            <>
              <Suspense fallback={<></>}>
                <ThemeWhite />
              </Suspense>
            </>
          ) : (
            <>
              <Suspense fallback={<></>}>
                <ThemeBold />
              </Suspense>
            </>
          )}
          <BrowserRouter>
            <DefaultTitle />
            <MainStemBase settings={appSettings} />
            <LeftNavOrganizationLogo logoURL={loggedInUser?.organization?.logoURL} />
            {/* <LayoutNew /> */}
            <AlertVersionUpgrade />
            <BetaInvite />
            <ToastContainer />
          </BrowserRouter>
        </FinancialDimensionCacheProvider>
      </AppInsightsContext.Provider>
    </AppContext.Provider>
  )
}

export default withAITracking(reactPlugin, Layout, '', 'hoc-root')
